import { ActivityEntityName, JobItemCommandId, StageItemDetail } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { EntityStatusUpdateComponent, type EntityStatusUpdateContext, type EntityStatusUpdateResult } from '@workbuddy/ui/blocks/entity.status.update';
import { PageMode } from '@workbuddy/ui/blocks/page';
import type { WorkBuddyDialogApi } from '@workbuddy/ui/v2/blocks/dialog';
import { firstValueFrom, lastValueFrom } from 'rxjs';

/**
 * One line's status move, shared by the Items board row ⋮ and the Items (old) tab row ⋮ (WBS-12289), so both
 * pick from the same centred dialog and write through the same JobItem commands.
 */
export async function pickItemStatus(dialogs: Pick<WorkBuddyDialogApi, 'create'>, context: EntityStatusUpdateContext): Promise<EntityStatusUpdateResult | undefined> {

  const ref = dialogs.create<EntityStatusUpdateResult, EntityStatusUpdateComponent>(EntityStatusUpdateComponent, {
    mode: 'modal',
    inputs: { context, sideDialog: false, layout: PageMode.Modal },
    outputs: {
      onSave: (chosen: EntityStatusUpdateResult) => ref.close(chosen),
      onCancel: () => ref.dismiss(),
    },
  });

  const chosen = await ref.result;
  return chosen?._id ? chosen : undefined;
}

/** The write the row editor makes, so the move answers to the same concurrency check and edit window. */
export async function writeItemStatusChange(entityData: EntityDataServiceRaw, itemId: string, chosen: EntityStatusUpdateResult): Promise<void> {

  const detail = await firstValueFrom(entityData.getById<StageItemDetail>(ActivityEntityName.JobItem, itemId));
  const stage = detail?.item?.stage;
  const patch = { itemStatus: { _id: chosen._id, name: chosen.name, reason: chosen.reason ? { _id: chosen.reason._id, name: chosen.reason.name } : undefined, note: chosen.note ?? undefined } };

  await lastValueFrom(entityData.executeCommand(ActivityEntityName.JobItem, JobItemCommandId.UpdateItem, { itemId, jobId: stage?.jobId, stageId: stage?._id, updatedAt: normalizeUpdatedAt(stage?.updatedAt), patch }));
}

export async function writeItemCancel(entityData: EntityDataServiceRaw, itemId: string, chosen: EntityStatusUpdateResult): Promise<void> {

  await lastValueFrom(entityData.executeCommand(ActivityEntityName.JobItem, JobItemCommandId.CancelItem, { itemId, statusId: chosen._id, reasonId: chosen.reason?._id, note: chosen.note }));
}

export function normalizeUpdatedAt(value: unknown): string | undefined {

  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}
